import { assayQueryKeys } from '@/config/queryKeys';
import { fetchAssayRunConfigs, fetchAssays, fetchProteoformResultsTableConfig } from '@/services/assays';
import { useQuery } from '@tanstack/react-query';

/**
 * Active assays from the catalog, suitable for populating a dropdown.
 * See POR-322 (backend) / POR-196 (Run Edit Form integration).
 */
export function useAssays() {
  return useQuery({
    queryKey: assayQueryKeys.list(),
    queryFn: fetchAssays,
    staleTime: 5 * 60 * 1000,
    // Don't retry: pre-deploy, this endpoint 404s (see POR-196/322 backend rollout), and
    // the global retry=1 default would needlessly delay the fallback UI these queries feed.
    retry: false,
  });
}

/**
 * Run configuration (valid species/sample counts/protein library counts + controls) for
 * every active assay. Intended for the Run create/edit page: fetch once, then look up the
 * config for whichever assay the user picks.
 */
export function useAssayRunConfigs() {
  return useQuery({
    queryKey: assayQueryKeys.runConfigs(),
    queryFn: fetchAssayRunConfigs,
    staleTime: 5 * 60 * 1000,
    // Don't retry: pre-deploy, this endpoint 404s (see POR-196/322 backend rollout), and
    // the global retry=1 default would needlessly delay the fallback UI these queries feed.
    retry: false,
  });
}

/**
 * Proteoform results table column layout (isoform/PTM columns + default threshold) for a
 * specific assay. Used by the Sample Details (Run Detail page) and Proteoform Abundances
 * (Sample Detail page) tables.
 */
export function useProteoformResultsTableConfig(assayId: string | undefined) {
  const normalizedAssayId = assayId ?? '';
  return useQuery({
    queryKey: assayQueryKeys.proteoformResultsTableConfig(normalizedAssayId),
    queryFn: () => fetchProteoformResultsTableConfig(normalizedAssayId),
    enabled: normalizedAssayId !== '',
    staleTime: 5 * 60 * 1000,
    // Don't retry: pre-deploy, this endpoint 404s (see POR-196/322 backend rollout), and
    // the global retry=1 default would needlessly delay the fallback UI these queries feed.
    retry: false,
  });
}
